import React, { useCallback, useState } from 'react';
import {
  IconButton,
  IconButtonProps,
} from '@lifeomic/chroma-react/components/IconButton';
import { Copy } from '@lifeomic/chromicons';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { CreateTemplatingItemModal } from '../Modals/CreateTemplatingItemModal';
import {
  ModelUtils,
  TemplatingItemDetail,
  TemplatingItemSummary,
} from '../../models';
import { useSelectedTemplatingItem } from '../../hooks/useSelectedTemplatingItem';
import { useIntl, defineMessages } from 'react-intl';
import { apiHooks } from '../../../../hooks/useRestAPIHooks';
import { useReportErrorToBugsnag } from '../../../../util/bugsnagReporting';
import { useTemplatingItemTypeDisplay } from '../../hooks/useTemplatingItemTypeDisplay';
import { clsx } from 'clsx';

const messages = defineMessages({
  copyTemplatingItem: {
    id: 'CreateTemplatingItemButton.copyTemplatingItem',
    defaultMessage: 'Copy {typeName}',
  },
});

const useStyles = makeStyles(() => ({
  copyTemplatingItemButton: {
    padding: 0,
  },
}));

type BaseCopyTemplatingItemProps = {
  templatingItem: TemplatingItemSummary;
  onSuccess?: (templatingItem: TemplatingItemDetail) => void;
  onDismiss?: () => void;
};

type CopyTemplatingItemButtonProps = BaseCopyTemplatingItemProps &
  Partial<IconButtonProps> & {
    className?: string;
  };

const useCopyTemplatingItemLogic = (
  sourceTemplatingItem: TemplatingItemSummary,
  onSuccessProp?: (templatingItem: TemplatingItemDetail) => void,
  onDismissProp?: () => void,
) => {
  const reportError = useReportErrorToBugsnag();
  const typeName = useTemplatingItemTypeDisplay()[sourceTemplatingItem.type];
  const intl = useIntl();
  const [isCreatingTemplatingItem, setIsCreatingTemplatingItem] =
    useState(false);
  const { setSelectedTemplatingItemId } = useSelectedTemplatingItem();

  const sourceTemplatingItemQuery = apiHooks.useAPIQuery(
    'GET /v1/ai-templates/:id',
    {
      id: sourceTemplatingItem.id,
    },
  );
  const updateTemplatingItemMutation = apiHooks.useAPIMutation(
    'PATCH /v1/ai-templates/:id',
  );

  const handleSuccess = useCallback(
    async (templatingItem: TemplatingItemDetail) => {
      try {
        if (!sourceTemplatingItemQuery.data) {
          // throw, it'll just create with a blank content
          throw new Error('Source template not found');
        }

        const sourceFullTemplatingItem =
          ModelUtils.templatingItemDetailFromRawData(
            sourceTemplatingItemQuery.data.template,
          );

        // Update the new template with the source template's content
        if ('content' in sourceFullTemplatingItem) {
          await updateTemplatingItemMutation.mutateAsync({
            type: templatingItem.type,
            id: templatingItem.id,
            content: sourceFullTemplatingItem.content,
          });
        } else {
          await updateTemplatingItemMutation.mutateAsync({
            type: templatingItem.type,
            id: templatingItem.id,
            sections: sourceFullTemplatingItem.sections,
          });
        }
      } catch (error) {
        console.error(error);
        reportError(error);
      } finally {
        setIsCreatingTemplatingItem(false);
        setSelectedTemplatingItemId(templatingItem.durableId);
        onSuccessProp?.(templatingItem);
      }
    },
    [
      sourceTemplatingItemQuery,
      updateTemplatingItemMutation,
      reportError,
      setSelectedTemplatingItemId,
      onSuccessProp,
    ],
  );

  const handleDismiss = useCallback(() => {
    setIsCreatingTemplatingItem(false);
    onDismissProp?.();
  }, [onDismissProp]);

  const openModal: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    event => {
      event.stopPropagation();
      event.preventDefault();
      setIsCreatingTemplatingItem(true);
    },
    [],
  );

  const copyTemplatingItemLabel = intl.formatMessage(
    messages.copyTemplatingItem,
    {
      typeName,
    },
  );

  return {
    isCreatingTemplatingItem,
    handleSuccess,
    handleDismiss,
    openModal,
    copyTemplatingItemLabel,
  };
};

export const CopyTemplatingItemButton: React.FC<
  CopyTemplatingItemButtonProps
> = ({ templatingItem, className, onSuccess, onDismiss, ...props }) => {
  const styles = useStyles();
  const {
    isCreatingTemplatingItem,
    handleSuccess,
    handleDismiss,
    openModal,
    copyTemplatingItemLabel,
  } = useCopyTemplatingItemLogic(templatingItem, onSuccess, onDismiss);

  return (
    <>
      <IconButton
        {...props}
        size={0}
        icon={Copy}
        aria-label={copyTemplatingItemLabel}
        onClick={openModal}
        className={clsx(className, styles.copyTemplatingItemButton)}
        color="inverse"
      />
      <CreateTemplatingItemModal
        folder={ModelUtils.getParentFolder(templatingItem)}
        isOpen={isCreatingTemplatingItem}
        templatingItem={templatingItem}
        onSuccess={handleSuccess}
        onDismiss={handleDismiss}
      />
    </>
  );
};
